import {type Extension, Prec} from '@codemirror/state'
import {type EditorView, keymap} from '@codemirror/view'
import {AutocompleteSuggestions} from '@github-ui/inline-autocomplete/AutocompleteSuggestions'
import type {Suggestion} from '@github-ui/inline-autocomplete/types'
import {getSuggestionKey} from '@github-ui/inline-autocomplete/utils'
import type {SuggestionOptions} from '@github-ui/markdown-editor/suggestions'
import type {Emoji} from '@github-ui/markdown-editor/suggestions/use-emoji-suggestions'
import type {Mentionable} from '@github-ui/markdown-editor/suggestions/use-mention-suggestions'
import type {Reference} from '@github-ui/markdown-editor/suggestions/use-reference-suggestions'
import {useId, useMemo} from 'react'

import {autocompleteListenerExtension} from '../codemirror/extensions/autocomplete-listener'
import {useCompartment} from '../codemirror/hooks/use-compartment'
import type {HybridEditorTelemetry} from '../telemetry'
import type {HybridMarkdownEditorRef} from '../types'
import {useAutocompleteState} from './use-autocomplete-state'
import {useStandardAutocompleteConfig} from './use-standard-autocomplete-config'

type UseAutocompleteResult = {
  autocompleteSuggestionsMenu: React.ReactElement | null
  autocompleteListenerExtension: Extension
  autocompleteKeymapCompartment: Extension
}

export const useAutocomplete = (
  ref: React.Ref<HybridMarkdownEditorRef>,
  view: EditorView | null,
  referenceSuggestions?: SuggestionOptions<Reference>,
  mentionSuggestions?: SuggestionOptions<Mentionable>,
  emojiSuggestions?: SuggestionOptions<Emoji>,
  emojiTone?: number,
  telemetry?: HybridEditorTelemetry,
): UseAutocompleteResult => {
  const {triggers, suggestions, setSuggestionEvent} = useStandardAutocompleteConfig(
    referenceSuggestions,
    mentionSuggestions,
    emojiSuggestions,
    emojiTone,
  )

  const {
    activeSuggestion,
    autocompleteKeymap,
    autocompleteVisible,
    insertAutocompleteSelection,
    setAutocompleteVisible,
    setTriggerCharCoords,
    showEventRef,
    triggerCharCoords,
  } = useAutocompleteState({
    suggestions,
    setSuggestionEvent,
    telemetry,
  })

  const listId = useId()
  const getSuggestionId = (suggestion: Suggestion) => `${listId}-${getSuggestionKey(suggestion)}`

  const insertAutocompleteToEditorRef = (suggestion = activeSuggestion) => {
    const editorRef = ref as React.RefObject<HybridMarkdownEditorRef>
    if (!editorRef.current?.view || !suggestion) return
    insertAutocompleteSelection(editorRef.current.view, suggestion)
  }

  // Use a compartment since we only want these keybindings when autocomplete suggestions are visible (i.e. the
  // compartment automatically reconfigures on that state change).
  const autocompleteKeyboardShortcutsCompartment = useCompartment(
    view,
    useMemo(
      () => (autocompleteVisible ? Prec.highest(keymap.of(autocompleteKeymap)) : []),
      [autocompleteVisible, autocompleteKeymap],
    ),
  )

  const hasSuggestions = suggestions !== null && suggestions.length > 0

  return {
    autocompleteSuggestionsMenu: (
      <AutocompleteSuggestions
        id={listId}
        suggestions={suggestions}
        inputRef={ref as React.RefObject<HybridMarkdownEditorRef>}
        onCommit={insertAutocompleteToEditorRef}
        onClose={() => {
          setAutocompleteVisible(false)
        }}
        triggerCharCoords={triggerCharCoords}
        visible={autocompleteVisible && hasSuggestions}
        defaultPlacement="below"
        activeSuggestion={activeSuggestion}
        getSuggestionId={getSuggestionId}
        role="listbox"
      />
    ),
    autocompleteKeymapCompartment: autocompleteKeyboardShortcutsCompartment,
    autocompleteListenerExtension: useCompartment(
      view,
      useMemo(
        () =>
          autocompleteListenerExtension(
            setSuggestionEvent,
            setAutocompleteVisible,
            setTriggerCharCoords,
            triggers,
            showEventRef,
          ),
        [setSuggestionEvent, setAutocompleteVisible, setTriggerCharCoords, triggers, showEventRef],
      ),
    ),
  }
}
